import { observer } from 'mobx-react';
import { action, computed, observable } from 'mobx';
import * as React from 'react';
import { inject, bind } from '../../../utils/decorators';
import { UiStore } from '../../../stores/UiStore';
import { ApiStore } from '../../../stores/ApiStore';
import { BpsReferenzkraftKalibrierungStore, IBpsReferenzkraftRollensatz, IBpsReferenzkraftRollensatzMesspunkt } from '../../../stores/BpsReferenzkraftKalibrierungStore';
import { Panel, Col, FormGroup, FormControl, ControlLabel, InputGroup, Table } from 'react-bootstrap';
import { IBpsReferenzkraftMesswerteResponse } from '../../../models/ApiTypesBps';
import BpsReferenzkraftVorbelastungRolle from './BpsReferenzkraftVorbelastungRolle';
import Callout from '../../site/Callout';
import { round } from '../../../utils/round';

@observer
class BpsReferenzkraftRollensatzMesspunkt extends React.Component<{ ziel: IBpsReferenzkraftRollensatzMesspunkt; onChangeHandler: () => void; validieren: boolean }, {}> {

  @inject(BpsReferenzkraftKalibrierungStore) store: BpsReferenzkraftKalibrierungStore;

  validate() {
    if (this.props.validieren) {
      return (this.props.ziel.messwerte.every(mw => mw !== null));
    }
    return true;
  }

  @bind @action handleMesswertChanged(index: number, ev: React.FormEvent<FormControl>) {
    this.props.ziel.messwerte[index] = (ev.target as HTMLInputElement).value !== '' ? (ev.target as HTMLInputElement).valueAsNumber : null;
    this.props.onChangeHandler();
  }

  render() {
    const { messwerte } = this.props.ziel;
    return (
      <tr>
        <td>{this.props.ziel.anzeige}</td>
        <td>{this.props.ziel.normal}</td>
        {messwerte.map((wert, index) =>
          <td key={index}>
            <FormGroup validationState={this.validate() ? undefined : 'error'} style={{margin: '0px'}}>
              <InputGroup>
                <FormControl
                  type="number"
                  step="any"
                  onChange={(e) => this.handleMesswertChanged(index, e)}
                  readOnly={this.store.readOnly}
                  value={(wert !== undefined && wert !== null) ? wert : ''}
                />
              </InputGroup>
            </FormGroup>
          </td>
        )}
        <td style={{fontSize: '10px'}}>{this.props.ziel.mittelwertabweichung && round(this.props.ziel.mittelwertabweichung, 3)}</td>
        <td style={{fontSize: '10px'}}>{this.props.ziel.messunsicherheit && round(this.props.ziel.messunsicherheit, 3)}</td>
        <td style={{fontSize: '10px'}}>{this.props.ziel.fehlergrenze}</td>
        <td><div className={this.props.ziel.konformitaet ? `konformitaet-${this.props.ziel.konformitaet.toLowerCase()}` : 'konformitaet-x'} style={{width: '48px'}}> {this.props.ziel.konformitaet ? this.props.ziel.konformitaet : '-'}</div></td>
      </tr>
    );
  }
}

@observer
class BpsReferenzkraftRollensatzMesswertEingabe extends React.Component<{ ziel: IBpsReferenzkraftRollensatz; }, {}> {

  @inject(ApiStore) apiStore: ApiStore;
  @inject(BpsReferenzkraftKalibrierungStore) store: BpsReferenzkraftKalibrierungStore;

  /**
   * Ermittelt ob der Nullpunkt komplett erfasst wurde
   * Dieser wird für die Betrachtung der Konformitätsaussagen benötigt
   */
  @computed get nullpunktVollstaendig() {
    const { ziel } = this.props;
    return ziel.messpunkte[0].messwerte.every(wert => wert !== null);
  }

  /**
   * Ermittelt ob neben dem Nullpunkt bereits weitere Messpunkte erfasst wurden
   * Wird genutzt um zu visualisieren dass die Beurteilung erst abgerufen wird wenn der Nullpunkt erfasst wurde
   */
  @computed get messpunkteNachNullpunktErfasst() {
    let werteVorhanden = false;
    this.props.ziel.messpunkte.forEach((mp, index) => {
      if ((index > 0) && (mp.messwerte.some(mw => mw !== null))) {
        werteVorhanden = true;
      }
    });
    return werteVorhanden;
  }

  render() {
    const { datensatz } = this.store;
    const { ziel } = this.props;
    if (ziel.messpunkte.length > 0 && this.store.messbereichGueltig()) {
      return (
        <div>
          {datensatz.anzeige === 'analog' && 'Einstellung am BPS, Anzeige am Normal'}
          {datensatz.anzeige === 'digital' && 'Einstellung am Normal, Anzeige am BPS'}
          <Table striped={false} bordered={false} condensed={true} hover={false} className="bps-auswertung">
            <thead>
              <tr>
                  <th>Anzeige</th>
                  <th>Normal</th>
                  <th>F<sub>1</sub></th>
                  <th>F<sub>2</sub></th>
                  <th>F<sub>3</sub></th>
                  <th>MWA</th>
                  <th>MU</th>
                  <th>FG</th>
                  <th style={{width: '48px'}}>Fall</th>
              </tr>
            </thead>
            <tbody>
              {ziel.messpunkte.map((mp, index) =>
                <BpsReferenzkraftRollensatzMesspunkt
                  key={index}
                  onChangeHandler={() => this.store.beurteilungAbrufenDelayed(ziel)}
                  ziel={mp}
                  validieren={index === 0 && this.messpunkteNachNullpunktErfasst}
                />
              )}
            </tbody>
          </Table>
        </div>
      );
    } else {
      return (
        <div>
          Die Messwerteingabe ist erst nach Auswahl der BPS-Daten möglich.
        </div>
      );
    }
  }
}

@observer
class BpsReferenzkraftRollensatz extends React.Component<{ ziel: IBpsReferenzkraftRollensatz; rollenDurchmesser: number | undefined;  }, {}> {
  render() {
    const { ziel } = this.props;
    let header = 'Rollensatz ' + ziel.position;
    return (
      <div>
        {/* Vorbelastung */}
        <Panel header={`${header} - Vorbelastung`} collapsible={true} defaultExpanded={true}>
          <BpsReferenzkraftVorbelastungRolle ziel={ziel}/>
        </Panel>
        {/* Eingabe Messwerte */}
        <Panel header={`${header} - Messung`} collapsible={true} defaultExpanded={true}>
          <BpsReferenzkraftRollensatzMesswertEingabe ziel={ziel}/>
        </Panel>
      </div>
    );
  }
}

/**
 * Exportierte Komponente für die Messwerteingabe und -auswertung der BPS Rollensätze links und rechts
 */
@observer
export default class BpsReferenzkraftRollensaetze extends React.Component<{}, {}> {

  @inject(UiStore) uiStore: UiStore;
  @inject(ApiStore) apiStore: ApiStore;
  @inject(BpsReferenzkraftKalibrierungStore) store: BpsReferenzkraftKalibrierungStore;

  @observable messpunkte: IBpsReferenzkraftMesswerteResponse = [];

  @bind @action setReferenzpunkte(referenzpunkte: IBpsReferenzkraftMesswerteResponse) {
    const { rollensatz } = this.store.datensatz;
    const anzahlReferenzpunkte = referenzpunkte.length;
    // Anzahl der Messpunkte der Rollensätze an Anzahl der Referenzmesspunkte anpassen
    if (referenzpunkte.length !== rollensatz.links.messpunkte.length || referenzpunkte.length || rollensatz.rechts.messpunkte.length) {
      [rollensatz.links, rollensatz.rechts].forEach(rs => {
        if (rs.messpunkte.length < referenzpunkte.length) {
          // Mehr Referenzpunkte als Messpunkte
          for (let i = rs.messpunkte.length; i < anzahlReferenzpunkte; i++) {
            rs.messpunkte.push({
              anzeige: 0,
              normal: 0,
              mittelwert: undefined,
              mittelwertabweichung: null,
              fehlergrenze: 0,
              messunsicherheit: null,
              messwerte: [null, null, null],
              konformitaet: null
            });
          }
        } else if (rs.messpunkte.length > anzahlReferenzpunkte) {
          rs.messpunkte = rs.messpunkte.slice(0, anzahlReferenzpunkte);
        }
        // Daten aus Referenzpunkten übernehmen
        referenzpunkte.forEach((rp, index) => {
          rs.messpunkte[index].anzeige = rp.anzeige;
          rs.messpunkte[index].normal = rs.position === 'links' ? rp.normal.links : rp.normal.rechts;
          rs.messpunkte[index].fehlergrenze = rp.fehlergrenze;
        });
      });
    }
  }

  @bind @action async messpunkteAbrufen() {
    const { datensatz, kalibrierGegenstand } = this.store;
    if (kalibrierGegenstand && kalibrierGegenstand.objekt && datensatz.messbereich && datensatz.rollendurchmesser.mittelwert.links && datensatz.rollendurchmesser.mittelwert.rechts) {
      const response = await this.store.messpunkteAbrufen();
      if (response && response.ok) {
        this.setReferenzpunkte(response.json);
      } else {
        // @todo: Hinweis/Ergebnis bereinigen
      }
    }
  }

  @bind @action handleMessbereichChanged(event: React.FormEvent<FormControl>) {
    this.store.setMessbereich((event.target as HTMLInputElement).value);
    this.messpunkteAbrufen();
    this.store.beurteilungAbrufenGesamt();
  }

  @bind @action async handleMessbereichKleinChanged(event: React.FormEvent<FormControl>) {
    let val = parseInt((event.target as HTMLInputElement).value, 10);
    this.store.setMessbereichKlein(!isNaN(val) ? val : undefined);
    this.messpunkteAbrufen();
    this.store.beurteilungAbrufenGesamt();
  }

  @bind @action handleMessbereichGrossChanged(event: React.FormEvent<FormControl>) {
    let val = parseInt((event.target as HTMLInputElement).value, 10);
    this.store.setMessbereichGross(!isNaN(val) ? val : undefined);
    this.messpunkteAbrufen();
    this.store.beurteilungAbrufenGesamt();
  }

  renderOptionen() {
    const { datensatz, messbereiche } = this.store;
    return (
      <div>
        <FormGroup controlId="Messbereich">
          <Col componentClass={ControlLabel} sm={4}>Messbereich</Col>
          <Col sm={4}>
            <FormControl componentClass="select" placeholder="select" onChange={this.handleMessbereichChanged}  value={datensatz.messbereich} disabled={this.store.readOnly}>
              <option value="">Bitte auswählen...</option>
              <option value={'klein'}>Klein</option>
              <option value={'groß'}>Groß</option>
              <option value={'kombiniert'}>Kombiniert</option>
            </FormControl>
          </Col>
        </FormGroup>
        {(datensatz.messbereich === 'klein' || datensatz.messbereich === 'kombiniert')  &&
          <FormGroup controlId="Messbereich (klein)">
            <Col componentClass={ControlLabel} sm={4}>Messbereich (klein)</Col>
            <Col sm={4}>
              <FormControl componentClass="select" placeholder="select" onChange={this.handleMessbereichKleinChanged} value={datensatz.messbereichKlein ? datensatz.messbereichKlein.toString() : undefined} disabled={this.store.readOnly}>
                <option value="">Bitte auswählen...</option>
                {messbereiche.map(wert => <option key={wert} value={wert}>{wert} kN</option>)}
              </FormControl>
            </Col>
          </FormGroup>
        }
        {(datensatz.messbereich === 'groß' || datensatz.messbereich === 'kombiniert')  &&
          <FormGroup controlId="Messbereich (groß)">
            <Col componentClass={ControlLabel} sm={4}>Messbereich (groß)</Col>
            <Col sm={4}>
              <FormControl componentClass="select" placeholder="select" onChange={this.handleMessbereichGrossChanged} value={datensatz.messbereichGross ? datensatz.messbereichGross.toString() : undefined} disabled={this.store.readOnly}>
                <option value="">Bitte auswählen...</option>
                {messbereiche.map(wert => <option key={wert} value={wert}>{wert} kN</option>)}
              </FormControl>
            </Col>
          </FormGroup>
        }
      </div>
    );
  }

  render() {
    const { datensatz } = this.store;
    let mittelwerteVorhanden = true;
    if (!datensatz.rollendurchmesser.mittelwert.links || !datensatz.rollendurchmesser.mittelwert.rechts) {
      mittelwerteVorhanden = false;
    }
    return (
      <div>
        <Panel header="Kalibrierung Rollensätze - BPS-Daten" collapsible={true} defaultExpanded={true}>
          {/* Optionen */}
          {mittelwerteVorhanden ?
            this.renderOptionen()
            :
            <div className="text-muted">Die Auswahl der BPS-Daten ist erst nach Eingabe der Rollendurchmesser möglich</div>
          }
        </Panel>
        {(mittelwerteVorhanden && this.store.messbereichGueltig()) ?
          <div>
            <BpsReferenzkraftRollensatz ziel={datensatz.rollensatz.links} rollenDurchmesser={datensatz.rollendurchmesser.mittelwert.links}/>
            <BpsReferenzkraftRollensatz ziel={datensatz.rollensatz.rechts} rollenDurchmesser={datensatz.rollendurchmesser.mittelwert.rechts}/>
          </div>
          :
          <Callout bsStyle="warning">
            Zur Messwerteingabe bitte Messbereiche auswählen.
          </Callout>
          }
      </div>
    );
  }

}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/vorlagen/bps/BpsReferenzkraftRollensaetze.tsx